import { Image as ImageIcon } from "@phosphor-icons/react";

const SAMPLE_DATA = {
  club_name: "Example Club",
  member_name: "First Last",
  member_id: "M-1001",
  member_email: "member@example.com",
  membership_status: "Active",
  expiry_date: "2027-03-31",
};

const FIELD_LABELS = {
  club_name: "Club",
  member_name: "Name",
  member_id: "Member ID",
  member_email: "Email",
  membership_status: "Status",
  expiry_date: "Valid Until",
};

const NONE_VALUE = "__none__";

const DEFAULT_LAYOUT = {
  header: "member_name",
  subheader: "membership_status",
  rows: [
    { items: [{ field: "member_photo", type: "image" }] },
    { items: [{ field: "member_id", type: "text" }, { field: "membership_status", type: "text" }] },
    { items: [{ field: "member_email", type: "text" }, { field: "expiry_date", type: "text" }] },
  ],
};

const GoogleWalletPreview = ({ design, compact = false, isExpired = false }) => {
  const layout = design.card_layout || DEFAULT_LAYOUT;
  const sampleData = {
    ...SAMPLE_DATA,
    membership_status: isExpired ? "Expired" : "Active",
  };

  const backgroundColor = isExpired
    ? (design.expired_color || "#71717A")
    : (design.active_color || design.background_color || "#16A34A");
  const textColor = design.text_color || "#FFFFFF";

  const getSample = (fieldId) => sampleData[fieldId] || "";
  const getLabel = (fieldId) => FIELD_LABELS[fieldId] || fieldId;

  return (
    <div className={compact ? "max-w-sm mx-auto" : "max-w-md mx-auto"} data-testid="wallet-preview">
      <div className={`rounded-2xl shadow-lg overflow-hidden ${compact ? "scale-75" : ""}`} style={{ backgroundColor }}>
        <div className={compact ? "p-4" : "p-6"}>
          {/* Logo + Club Name */}
          <div className="flex items-center gap-3 mb-4">
            {design.logo_url && (
              <img
                src={design.logo_url}
                alt="Logo"
                className={compact ? "h-9 w-9 object-contain rounded-full bg-white/20 p-0.5" : "h-12 w-12 object-contain rounded-full bg-white/20 p-0.5"}
                data-testid="preview-logo"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <span className={`${compact ? "text-sm" : "text-base"} font-medium opacity-90`} style={{ color: textColor }}>
              {design.club_name || "Club Name"}
            </span>
          </div>

          {/* Subheader */}
          {layout.subheader && layout.subheader !== NONE_VALUE && (
            <p className={`${compact ? "text-sm" : "text-base"} font-semibold mb-1`} style={{ color: textColor }}>
              {getSample(layout.subheader)} Member
            </p>
          )}

          {/* Header */}
          <p
            className={compact ? "text-xl font-bold leading-tight mb-4" : "text-2xl font-bold leading-tight mb-4"}
            style={{ color: textColor }}
            data-testid="preview-member-name"
          >
            {getSample(layout.header) || "Header"}
          </p>

          {/* Dynamic Rows */}
          {layout.rows.map((row, rowIdx) => (
            <div key={rowIdx} className={`border-t border-white/20 pt-2 pb-1 ${row.items.length === 2 ? "grid grid-cols-2 gap-4" : ""}`}>
              {row.items.map((item, itemIdx) => {
                if (!item.field || item.field === NONE_VALUE) return <div key={itemIdx} />;
                if (item.type === "image") {
                  return (
                    <div key={itemIdx} className="flex justify-center py-1" data-testid="preview-photo">
                      <div className={`${compact ? "w-10 h-10" : "w-12 h-12"} rounded-md overflow-hidden bg-white/10 flex items-center justify-center`}>
                        <ImageIcon size={compact ? 16 : 20} style={{ color: textColor }} className="opacity-60" />
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={itemIdx}>
                    <p className="text-xs opacity-60 mb-0.5" style={{ color: textColor }}>
                      {getLabel(item.field)}
                    </p>
                    <p className={`${compact ? "text-xs" : "text-sm"} font-medium break-all`} style={{ color: textColor }}>
                      {getSample(item.field)}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Banner */}
        {design.banner_image_url && (
          <div data-testid="banner-image">
            <img
              src={design.banner_image_url}
              alt="Banner"
              className="w-full h-28 object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        )}
      </div>

      {!compact && (
        <div className="mt-3 text-center">
          <p className="text-xs text-zinc-500">
            {isExpired ? (
              <span className="text-red-600 font-medium">Expired Pass Preview</span>
            ) : (
              <span className="text-green-600 font-medium">Active Pass Preview</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleWalletPreview;
